import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ShieldCheck, Scale, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { captureTracking, getSessionId, isMetaVisitor } from '@/lib/tracking';
import { useFunnel } from '@/context/FunnelContext';
import { COMPANY } from '@/lib/siteContent';
import { tr } from '@/lib/i18n';
import { JsonLd } from '@/components/JsonLd';
import { legalServiceSchema, faqSchemaEn, faqSchemaEs } from '@/lib/seoSchemas';
import { useSEO } from '@/lib/useSEO';

// Shared landing page. Renders the English home (`/`) or the Spanish page (`/sp`)
// based on props. `sourcePage` tags clicks/leads ('home' vs 'sp'); `pageLang`
// drives all copy + the funnel language.
// localStorage cache keys. We stash the last-known-good hook/content payload
// so repeat visitors see the real values IMMEDIATELY (no flash-of-defaults).
// The cache is per-language + per-source-page so /sp doesn't overwrite /'s
// English cache and vice versa.
const _cacheKey = (kind, lang, source) => `lp:${kind}:${source}:${lang}`;
const _readCache = (kind, lang, source) => {
  try {
    const v = JSON.parse(localStorage.getItem(_cacheKey(kind, lang, source)) || 'null');
    if (v && typeof v === 'object') return v;
  } catch (e) { /* ignore */ }
  return null;
};
const _writeCache = (kind, lang, source, value) => {
  try { localStorage.setItem(_cacheKey(kind, lang, source), JSON.stringify(value)); } catch (e) { /* ignore */ }
};

export default function Landing({ sourcePage = 'home', pageLang = 'en' }) {
  // Per-route SEO. /sp (Spanish) gets fully localized meta so Google treats
  // it as its own indexable document instead of a near-duplicate of /.
  useSEO(
    pageLang === 'es'
      ? 'Abogados de Ley Limón de California | Revisión Gratuita de Caso — The Lemon Pros'
      : 'The Lemon Pros — California Lemon Law Attorneys | Free Case Review',
    pageLang === 'es'
      ? "¿Problemas con un auto defectuoso en California? Nuestros abogados de ley limón obligan al fabricante a reembolsarte, reemplazarlo o pagarte efectivo. Revisión gratuita en 60 segundos. Sin honorarios a menos que ganemos."
      : "California lemon law firm. We force automakers to buy back, replace, or pay cash for defective vehicles. Free 60-second case review. 100s of cases won. No fee unless we win.",
    pageLang === 'es' ? '/sp' : '/',
  );
  const navigate = useNavigate();
  const { setAnswer, resetAnswers, setLang } = useFunnel();
  const t = tr(pageLang);
  const [loaded, setLoaded] = useState(false);
  // Hydrate from cached admin-configured values on first render so returning
  // visitors don't see the default hooks flash to CMS hooks.
  //
  // Once the initial `hooks` state is set for this page load it NEVER
  // changes — even if /config/public later returns a different admin
  // headline. Reason: the previous flow visibly swapped the hero copy
  // in front of the customer (default → CMS hook), which reads as a
  // bug when someone is watching the screen. The freshly-returned
  // hooks are stashed in localStorage so the visitor sees them on
  // their NEXT visit, but the paint they're looking at right now
  // stays locked from the first frame.
  const [hooks] = useState(() => {
    const cached = _readCache('hooks', pageLang, sourcePage);
    if (cached && cached.hook1 && cached.hook2) return cached;
    return { hook1: t.landing.defaultHook1, hook2: t.landing.defaultHook2 };
  });
  // Content (button label / tooltip / trust chips) follows the same rule:
  // whatever we paint on frame one is what the customer sees for this
  // session. The API response only updates localStorage for next time.
  const [content] = useState(() => {
    const cached = _readCache('content', pageLang, sourcePage);
    const defaults = {
      tooltip: t.landing.tooltip,
      cta: t.landing.cta,
      rated: t.landing.rated,
      free_consult: t.landing.freeConsult,
      no_win_no_fee: t.landing.noWinNoFee,
    };
    return cached ? { ...defaults, ...cached } : defaults;
  });

  useEffect(() => {
    let mounted = true;
    setLang(pageLang);

    const tracking = captureTracking(window.location.search);
    const sessionId = getSessionId();
    api
      .post('/track/click', {
        session_id: sessionId,
        landing_path: window.location.pathname,
        source_page: sourcePage,
        campaign_id: tracking.campaign_id,
        adgroup_id: tracking.adgroup_id,
        ad_id: tracking.ad_id,
        keyword: tracking.keyword,
        gclid: tracking.gclid,
        gbraid: tracking.gbraid,
        wbraid: tracking.wbraid,
        referrer: tracking.referrer,
        feeditemid: tracking.feeditemid,
        extensionid: tracking.extensionid,
        split_experiment_id: tracking.split_experiment_id,
        split_variant: tracking.split_variant,
        params: tracking.params,
      })
      .catch(() => {});

    const qs = new URLSearchParams({
      campaign: tracking.campaign_id || '',
      adgroup: tracking.adgroup_id || '',
      ad: tracking.ad_id || '',
      session: sessionId || '',
      lang: pageLang === 'es' ? 'es' : '',
    }).toString();
    api
      .get(`/config/public?${qs}`)
      .then((res) => {
        if (mounted) {
          // Cache the fresh hooks for the visitor's NEXT visit — but do
          // NOT setHooks(): the paint the customer is looking at right
          // now must not visibly change mid-view. (See the comment on the
          // useState initializer above.)
          const next = { hook1: res.data.hook1, hook2: res.data.hook2 };
          if (next.hook1 && next.hook2) {
            _writeCache('hooks', pageLang, sourcePage, next);
          }
          setLoaded(true);
        }
      })
      .catch(() => {
        if (mounted) setLoaded(true);
      });
    api
      .get(`/page-content/${sourcePage === 'sp' ? 'sp' : 'home'}`)
      .then((res) => {
        // Same rule as hooks — cache-only. The tooltip / CTA / trust
        // chips visible right now stay put; the freshly returned copy
        // is used on the next visit.
        if (mounted && res.data) {
          _writeCache('content', pageLang, sourcePage, res.data);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
    // Deliberately empty deps: this effect fires once per landing mount
    // so tracking is only sent once. The `setHooks` / `setContent` calls
    // that previously depended on `pageLang` / `sourcePage` are now
    // cache-only — see the useState initializers above.
  }, []);

  const start = () => {
    resetAnswers();
    setLang(pageLang);
    setAnswer('started', '1');
    setAnswer('source_page', sourcePage);
    // Track the entry landing page so the funnel "back to home" returns
    // exactly where the visitor started (/ or /sp), not the wrong-language
    // default.
    setAnswer('entry_path', typeof window !== 'undefined' ? window.location.pathname : (pageLang === 'es' ? '/sp' : '/'));
    // Stash entry_phone so the funnel header + thank-you inherit the SAME
    // number the visitor saw on the landing page. On the English home, swap
    // to the dedicated Facebook line when the visitor arrived from Meta.
    if (pageLang !== 'es' && isMetaVisitor()) {
      setAnswer('entry_phone', COMPANY.phoneFbEn);
      setAnswer('entry_phone_href', COMPANY.phoneHrefFbEn);
    }
    api.post('/track/engage', { session_id: getSessionId() }).catch(() => {});
    navigate('/flow/year');
  };

  return (
    <div className="max-w-4xl mx-auto px-4 pt-8 sm:pt-14 text-center" data-testid="page-landing">
      {/* AI-visibility: per-page FAQ (language-aware).
          LegalService lives in /app/frontend/public/index.html so it's
          present in the raw HTML for every crawler. */}
      <JsonLd data={pageLang === 'es'
        ? faqSchemaEs('https://apply.thelemonpros.com/sp')
        : faqSchemaEn('https://apply.thelemonpros.com/')} />
      {/* Hero paints IMMEDIATELY with default hooks — no opacity gate on the API
          response. LCP used to wait for /config/public to return (~2-4s on 3G)
          which tanked mobile Lighthouse LCP to 11+ seconds. Now the fallback
          copy renders synchronously and admin-configured hooks swap in the
          moment they arrive (no visible flash — same font, same layout). */}
      <div>
        <h1
          className="font-mock font-extrabold tracking-tight text-[#0F1B3D] leading-[1.05] text-[clamp(2.1rem,5.6vw,4rem)]"
          data-testid="hero-hook1"
        >
          {hooks.hook1}
        </h1>
        <p
          className="mt-4 mx-auto max-w-2xl font-semibold text-slate-700 text-[clamp(1.02rem,2.1vw,1.35rem)]"
          data-testid="hero-hook2"
        >
          {hooks.hook2}
        </p>
      </div>

      <div className="mt-7 sm:mt-9 mx-auto w-full max-w-xl">
        <div className="relative bg-white rounded-2xl shadow-[0_18px_50px_rgba(15,27,61,0.18)] px-5 pt-7 pb-5">
          <button
            onClick={start}
            className="group h-14 w-full px-7 rounded-xl font-bold text-lg bg-[#EF4444] hover:bg-[#DC2626] text-white shadow-[0_10px_24px_rgba(239,68,68,0.35)] transition-colors flex items-center justify-center gap-2"
            data-testid="hero-get-started-button"
          >
            {content.cta}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs sm:text-sm text-slate-500" data-testid="hero-trust-line">
            <span className="flex items-center gap-1.5"><Star className="h-4 w-4 text-amber-400 fill-amber-400" /> {content.rated}</span>
            <span className="flex items-center gap-1.5" data-testid="hero-badge-free-consultation"><ShieldCheck className="h-4 w-4 text-emerald-500" /> {content.free_consult}</span>
            <span className="flex items-center gap-1.5"><Scale className="h-4 w-4 text-blue-500" /> {content.no_win_no_fee}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
